import { useMemo } from 'react';
import { clientIsHighRisk, documentsDueForReview, findClientForJob, isOpen, latestArmpByJob, sgarCount, sgarDue } from '../../data/domain';
import { useClients, useDocuments, useJobRecords, useJobs, useRodentPlans } from '../../data/hooks';
import type { Job, JobRecord } from '../../data/types';

export interface AppNotification {
  id: string;
  title: string;
  detail: string;
  type: 'review' | 'risk' | 'jobs';
  action: { kind: 'navigate'; path: string } | { kind: 'armp-review'; jobKey: string } | { kind: 'rodent-plan'; planId: string };
}

export const armpJobKey = (job: Job) => job.id;
export const armpRecordKey = (record: JobRecord) => record.jobId || `${record.clientName}|${record.jobDate}|${record.jobTime}|${record.jobService}`;

/** ARMP review items: unconfirmed ARMPs, and open high-risk jobs that have no ARMP yet. */
export function useArmpReviewItems() {
  const records = useJobRecords();
  const jobs = useJobs();
  const clients = useClients();
  return useMemo(() => {
    const latest = latestArmpByJob(records);
    const items: Array<{ jobKey: string; clientName: string; record: JobRecord | null; job: Job | null }> = [];
    for (const [jobKey, record] of latest) {
      const client = clients.find((item) => item.id === record.clientId) || clients.find((item) => item.name === record.clientName);
      if (!clientIsHighRisk(client) || record.review?.status === 'confirmed') continue;
      items.push({ jobKey, clientName: record.clientName, record, job: jobs.find((job) => job.id === record.jobId) || null });
    }
    for (const job of jobs.filter(isOpen)) {
      if (!clientIsHighRisk(findClientForJob(job, clients)) || latest.has(armpJobKey(job))) continue;
      items.push({ jobKey: armpJobKey(job), clientName: job.clientName, record: null, job });
    }
    return items;
  }, [records, jobs, clients]);
}

export function useNotifications(): AppNotification[] {
  const documents = useDocuments();
  const plans = useRodentPlans();
  const jobs = useJobs();
  const armpItems = useArmpReviewItems();
  return useMemo(() => {
    const messages: AppNotification[] = documentsDueForReview(documents).map((doc) => ({ id: `review-${doc.id}-${doc.expiry}`, title: doc.documentName || doc.name, detail: 'Compliance document review is due or overdue.', type: 'review', action: { kind: 'navigate', path: '/compliance' } }));
    for (const plan of plans) {
      if (!sgarCount(plan)) continue;
      const { days, due } = sgarDue(plan);
      if (days !== null && days > 7) continue;
      const timing = !due || days === null ? 'Review date is not recorded.' : days < 0 ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}.` : days === 0 ? 'Due today.' : `Due in ${days} day${days === 1 ? '' : 's'}.`;
      messages.push({ id: `sgar-review-${plan.id}-${plan.lastSiteReviewDate || plan.savedAt}`, title: `${plan.clientName}: SGAR site review`, detail: `${timing} Site review is required at least every 35 days.`, type: 'review', action: { kind: 'rodent-plan', planId: plan.id } });
    }
    for (const item of armpItems) {
      messages.push({
        id: `armp-review-${item.jobKey}-${item.record?.id || 'required'}`,
        title: `${item.clientName}: ${item.record ? 'confirm ARMP review' : 'update and confirm ARMP'}`,
        detail: item.record ? 'A newly created ARMP requires confirmation by an operations or administrator account.' : 'A high-risk service has been booked. Create, update and confirm its ARMP before attendance.',
        type: 'risk',
        action: { kind: 'armp-review', jobKey: item.jobKey }
      });
    }
    const open = jobs.filter(isOpen).length;
    if (open) messages.push({ id: `jobs-${open}`, title: `${open} open service${open === 1 ? '' : 's'} require attention`, detail: 'Scheduled work remains uncompleted.', type: 'jobs', action: { kind: 'navigate', path: '/schedule' } });
    return messages;
  }, [documents, plans, jobs, armpItems]);
}
